import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaTrophy, FaMusic, FaUsers, FaStar, FaPlay, FaCompactDisc } from 'react-icons/fa';
import '../styles/RankingsPage.css';

const API = process.env.REACT_APP_API_URL || '';

function RankingsPage({ playTrack }) {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('30');
  const [tab, setTab] = useState('albums');
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadRankings(); }, [period]);

  const loadRankings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/rankings`, { params: { period } });
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const getCoverUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API}${url.startsWith('/') ? url : '/' + url}`;
  };

  const formatDuration = (s) => s ? `${Math.floor(s/60)}:${(s%60).toString().padStart(2,'0')}` : '';

  const medalColor = (i) => i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : null;

  if (loading) return <div className="rankings-page"><div className="rankings-loading">Chargement...</div></div>;
  if (!data) return null;

  return (
    <div className="rankings-page">
      <div className="rankings-header">
        <h1><FaTrophy /> Classements</h1>
        <div className="period-select">
          <button className={period === '7' ? 'active' : ''} onClick={() => setPeriod('7')}>7 jours</button>
          <button className={period === '30' ? 'active' : ''} onClick={() => setPeriod('30')}>30 jours</button>
          <button className={period === '90' ? 'active' : ''} onClick={() => setPeriod('90')}>3 mois</button>
          <button className={period === '365' ? 'active' : ''} onClick={() => setPeriod('365')}>1 an</button>
        </div>
      </div>

      <div className="rankings-tabs">
        <button className={tab === 'albums' ? 'active' : ''} onClick={() => setTab('albums')}>
          <FaCompactDisc /> Top Albums
        </button>
        <button className={tab === 'tracks' ? 'active' : ''} onClick={() => setTab('tracks')}>
          <FaMusic /> Top Pistes
        </button>
        <button className={tab === 'users' ? 'active' : ''} onClick={() => setTab('users')}>
          <FaUsers /> Top Auditeurs
        </button>
        <button className={tab === 'rated' ? 'active' : ''} onClick={() => setTab('rated')}>
          <FaStar /> Mieux notés
        </button>
      </div>

      {/* TOP ALBUMS */}
      {tab === 'albums' && (
        <div className="ranking-list">
          {data.topAlbums.length === 0 ? <div className="ranking-empty">Pas encore de données</div> :
            data.topAlbums.map((a, i) => (
              <div key={a.id} className="ranking-item">
                <div className="rank-num" style={medalColor(i) ? {color: medalColor(i)} : {}}>
                  {i < 3 ? '🏅' : i + 1}
                </div>
                {a.cover_url && <img src={getCoverUrl(a.cover_url)} alt="" className="rank-cover" />}
                <div className="rank-info">
                  <div className="rank-title">{a.title}</div>
                  <div className="rank-sub">{a.artist}</div>
                </div>
                <div className="rank-stats">
                  <div className="rank-stat-main">{a.play_count} <span>écoutes</span></div>
                  <div className="rank-stat-sub">{a.listener_count} auditeurs</div>
                </div>
              </div>
            ))
          }
        </div>
      )}

      {/* TOP PISTES */}
      {tab === 'tracks' && (
        <div className="ranking-list">
          {data.topTracks.length === 0 ? <div className="ranking-empty">Pas encore de données</div> :
            data.topTracks.map((t, i) => (
              <div key={t.id} className="ranking-item clickable" onClick={() => playTrack && playTrack(t)}>
                <div className="rank-num" style={medalColor(i) ? {color: medalColor(i)} : {}}>
                  {i < 3 ? '🏅' : i + 1}
                </div>
                {t.cover_url && <img src={getCoverUrl(t.cover_url)} alt="" className="rank-cover" />}
                <div className="rank-info">
                  <div className="rank-title">{t.title}</div>
                  <div className="rank-sub">{t.artist} — {t.album_title}</div>
                </div>
                <div className="rank-stats">
                  <div className="rank-stat-main">{t.play_count} <span>écoutes</span></div>
                  <div className="rank-stat-sub">{formatDuration(t.duration)}</div>
                </div>
                <FaPlay className="rank-play" />
              </div>
            ))
          }
        </div>
      )}

      {/* TOP USERS */}
      {tab === 'users' && (
        <div className="ranking-list">
          {data.topUsers.length === 0 ? <div className="ranking-empty">Pas encore de données</div> :
            data.topUsers.map((u, i) => (
              <div key={u.id} className="ranking-item">
                <div className="rank-num" style={medalColor(i) ? {color: medalColor(i)} : {}}>
                  {i < 3 ? '🏅' : i + 1}
                </div>
                <div className="rank-avatar">{(u.display_name || u.username).charAt(0).toUpperCase()}</div>
                <div className="rank-info">
                  <div className="rank-title">{u.display_name || u.username}</div>
                  <div className="rank-sub">@{u.username}</div>
                </div>
                <div className="rank-stats">
                  <div className="rank-stat-main">{u.play_count} <span>écoutes</span></div>
                  <div className="rank-stat-sub">{u.album_count} albums</div>
                </div>
              </div>
            ))
          }
        </div>
      )}

      {/* MIEUX NOTÉS */}
      {tab === 'rated' && (
        <div className="ranking-list">
          {data.topRated.length === 0 ? <div className="ranking-empty">Pas assez de notes (min. 2)</div> :
            data.topRated.map((a, i) => (
              <div key={a.id} className="ranking-item">
                <div className="rank-num" style={medalColor(i) ? {color: medalColor(i)} : {}}>
                  {i < 3 ? '🏅' : i + 1}
                </div>
                {a.cover_url && <img src={getCoverUrl(a.cover_url)} alt="" className="rank-cover" />}
                <div className="rank-info">
                  <div className="rank-title">{a.title}</div>
                  <div className="rank-sub">{a.artist}</div>
                </div>
                <div className="rank-stats">
                  <div className="rank-stat-main">{'⭐'.repeat(Math.round(a.avg_rating))} {a.avg_rating}</div>
                  <div className="rank-stat-sub">{a.rating_count} votes</div>
                </div>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

export default RankingsPage;
